import { useT } from '../i18n';
import { ROUTES } from '../lib/router';
import type { ApartmentInfo, OwnerContact, SubmissionFiles } from '../domain/types';
import { parseDecimal, photosMissingSeverity, type Errors } from '../lib/validation';
import { Field } from './Field';
import './StepForm.css';

interface StepContactProps {
  contact: OwnerContact;
  apartment: ApartmentInfo;
  files: SubmissionFiles;
  errors: Errors;
  onContactChange: (update: Partial<OwnerContact>) => void;
  /** Takes the owner back to step 2 to add the photos they skipped. */
  onGoAddPhotos: () => void;
}

const nf = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 });

export function StepContact({
  contact,
  apartment,
  files,
  errors,
  onContactChange,
  onGoAddPhotos,
}: StepContactProps) {
  const t = useT();

  const suprafata = parseDecimal(apartment.suprafataUtila);
  const anLabel =
    apartment.anConstructie === ''
      ? t.step3.summaryEmpty
      : t.step1.anConstructieOptions[apartment.anConstructie];
  const photoSeverity = photosMissingSeverity(files);

  return (
    <div className="step-form step-form--wide">
      <header className="step-form__intro">
        <h2>{t.step3.heading}</h2>
        <p className="lede">{t.step3.lede}</p>
      </header>

      {/*
        "Ce primiți" is NOT here. It now closes the acknowledgement instead — see
        Acknowledgement.tsx — where it is read after the commitment rather than as
        an argument for making it.
      */}
      <div className="step-form__stack">
        <div className="step-contact__top">
          <div className="step-form__fields">
            <Field name="name" label={t.step3.nameLabel} error={errors['name']}>
              {(props) => (
                <input
                  {...props}
                  className="input"
                  type="text"
                  autoComplete="name"
                  placeholder={t.step3.namePlaceholder}
                  value={contact.name}
                  onChange={(e) => onContactChange({ name: e.target.value })}
                />
              )}
            </Field>

            <Field name="email" label={t.step3.emailLabel} error={errors['email']}>
              {(props) => (
                <input
                  {...props}
                  className="input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  autoCapitalize="off"
                  placeholder={t.step3.emailPlaceholder}
                  value={contact.email}
                  onChange={(e) => onContactChange({ email: e.target.value })}
                />
              )}
            </Field>

            <Field name="phone" label={t.step3.phoneLabel} help={t.step3.phoneHelp} error={errors['phone']}>
              {(props) => (
                <input
                  {...props}
                  className="input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  spellCheck={false}
                  placeholder={t.step3.phonePlaceholder}
                  value={contact.phone}
                  onChange={(e) => onContactChange({ phone: e.target.value })}
                />
              )}
            </Field>
          </div>

          <section className="summary-block" aria-labelledby="summary-heading">
            <h3 className="summary__heading" id="summary-heading">
              {t.step3.summaryHeading}
            </h3>
            <dl className="summary summary__list">
              <div className="summary__row">
                <dt className="summary__key">{t.step3.summaryAddress}</dt>
                <dd className="summary__value">{apartment.address || t.step3.summaryEmpty}</dd>
              </div>
              <div className="summary__row">
                <dt className="summary__key">{t.step3.summarySuprafata}</dt>
                <dd className="summary__value">
                  {suprafata === null
                    ? t.step3.summaryEmpty
                    : t.step3.summarySuprafataValue(nf.format(Math.round(suprafata)))}
                </dd>
              </div>
              <div className="summary__row">
                <dt className="summary__key">{t.step3.summaryAn}</dt>
                <dd className="summary__value">{anLabel}</dd>
              </div>
              <div className="summary__row">
                <dt className="summary__key">{t.step3.summaryPhotos}</dt>
                <dd className="summary__value">
                  {t.step3.summaryPhotosCount(files.photos.length)}
                  {files.plan && <span className="summary__note">{t.step3.summaryPlanAttached}</span>}
                </dd>
              </div>
            </dl>
          </section>
        </div>

        {/*
          A notice, not an error. The submission is valid without photos, so this
          uses `role="note"`, states what more photos give, and offers the way
          back — it must never read as a blocker.
        */}
        {photoSeverity && (
          <aside className="notice" role="note">
            <h3 className="notice__heading">
              {photoSeverity === 'none' ? t.step2.photosNoneHeading : t.step2.photosFewHeading}
            </h3>
            <p className="notice__body">{t.step2.photosFewBody}</p>
            <button type="button" className="btn btn--quiet notice__action" onClick={onGoAddPhotos}>
              {t.step2.photosFewAction}
            </button>
          </aside>
        )}

        {/* The whole label is the hit target, checkbox and legal text together. */}
        <label className={errors['consent'] ? 'consent consent--error' : 'consent'}>
          <input
            className="consent__box"
            type="checkbox"
            data-field="consent"
            checked={contact.consent}
            aria-invalid={Boolean(errors['consent'])}
            aria-describedby={errors['consent'] ? 'consent-error' : undefined}
            onChange={(e) => onContactChange({ consent: e.target.checked })}
          />
          <span className="consent__text">
            {t.step3.consentText}{' '}
            <a href={ROUTES.privacy} target="_blank" rel="noopener noreferrer">
              {t.step3.consentLink}
            </a>
          </span>
        </label>

        {errors['consent'] && (
          <p className="field__error" id="consent-error">
            <span className="visually-hidden">{t.validation.fieldError}</span>
            <span aria-hidden="true">↳</span>
            {errors['consent']}
          </p>
        )}
      </div>
    </div>
  );
}
